import { gsap } from 'gsap';
import { lightbox } from '../components/lightbox';
import { readGlobalState } from '../util/helpers';
import { mq } from '../util/matchMedia';

// GLOSSARY:
// -> Gallery: the section of the site
// -> Collection: each set of 6 media items within the Gallery section
// -> Carousel: the automated transition between Collections

export const carousel = (() => {
  const INTERVAL = 3; // Delay between slides
  const SLIDE_DURATION = 4; // Duration of entire slide animation
  const FADE_DURATION = 1; // Duration of each media item fade
  const ANIMATION_ORDER_DESKTOP = [0, 1, 2, 5, 4, 3]; // Order of images (clockwise)
  const ANIMATION_ORDER_MOBILE = [2, 3, 5, 4, 1, 0]; 

  let animationOrder, carouselTimeline, collections, allMediaItems;

  const init = () => {
    getReferenceElements();
    createCarouselTimeline();
  }

  const getReferenceElements = () => {
    allMediaItems = document.querySelectorAll('.js-gallery-media');
    collections = document.querySelectorAll(`.js-gallery-${readGlobalState('mq')} .js-gallery-collection`);
  }

  const createCarouselTimeline = () => {
    animationOrder = mq.isDesktop ? ANIMATION_ORDER_DESKTOP : ANIMATION_ORDER_MOBILE;
    // If there's only one collection, don't repeat.
    const repeatAmount = collections.length < 2 ? 0 : -1;

    carouselTimeline = gsap.timeline({
      paused: true,
      repeat: repeatAmount,
      delay: 0,
      repeatDelay: INTERVAL, // Delay before it loops again
      onStart: () => {
        // Collections hidden by default until first slide has animated
        gsap.to(collections, {
          autoAlpha: 1,
          delay: FADE_DURATION + SLIDE_DURATION,
        })
      },
    });

    collections.forEach((collection, index) => {
      const delay = index ? INTERVAL : 0;
      carouselTimeline.add(animateCollection(collection, index), `+=${delay}`);
    });
  }

  const animateCollection = (collection, index) => {
    if (!collection) return;
    const media = collection.querySelectorAll('.js-gallery-media');

    const collectionTimeline = gsap.timeline({
      onComplete: playVideo,
      onCompleteParams: [ collection ],
    });

    collectionTimeline
      .set(allMediaItems, { pointerEvents: 'none' })

      .set(collection, {
        autoAlpha: 1,
        zIndex: 2,
        onComplete: lightbox.loadCollection,
        onCompleteParams: [index],
      })

      .set(media, { autoAlpha: 0 })

      .to(media, {
        autoAlpha: 1,
        duration: FADE_DURATION,
        ease: 'Power1.inOut',
        stagger: function (index) {
          const factor = animationOrder.indexOf(index) + 1;
          return factor * (SLIDE_DURATION / 6);
        },
      })

      .set(media, { pointerEvents: 'auto' })

      .set(collection, { zIndex: 1 })
    
    return collectionTimeline;
  }

  const playVideo = (collection) => {
    const video = collection.querySelector('.js-gallery-media-video');
    const poster = collection.querySelector('.js-gallery-media-poster');
    if (!video || !poster) return;

    if (readGlobalState('lightboxOpen')) return;
    carouselTimeline.pause();

    const play = video.play.bind(video);

    const tl = gsap.timeline({ delay: 1.25 });
      tl
        .to(poster, { autoAlpha: 0, duration: 1 })
        .call(play, null, '>-1')
    
    video.addEventListener('ended', () => {
      if (readGlobalState('lightboxOpen')) return;

      gsap.to(poster, { autoAlpha: 1, duration: 1 });
      // Skip forward 1.5 seconds, to switch slides sooner
      carouselTimeline.play(carouselTimeline.time() + 1.5);
      
    });
  }


  const play = () => carouselTimeline.play();
  const pause = () => carouselTimeline.pause();
  const resume = () => carouselTimeline.resume();

  return { init, play, pause, resume }
})();